import { useEffect, useState, useRef } from 'react';
import { api } from '../api.js';
import { getDisplayUnits } from '../units.js';
import { readSalinityUnit } from '../salinity.js';
import { OPEN_SETTINGS_EVENT } from '../app-open.js';
import { probePickMessage, probeReading, tileLabels } from '../probe-pick.js';

// Live HYDROS controller card for Home. Polls /api/tanks/:id/hydros on a
// short cadence (every 30s while the tab is visible; paused when hidden
// so a backgrounded phone doesn't spam the API). The endpoint is fed by
// the in-process HYDROS store, so latency on a hit is sub-100ms and
// safe to call this often — it never touches the HYDROS cloud.
//
// What's surfaced today:
//   - controller online + last_seen
//   - probe values (temp, ph, salinity, alk, orp) — each with its
//     HYDROS alert level (0/1/4/8 → none/yellow/orange/red) shown as a
//     coloured pill so a reefer sees a probe alarm without opening the
//     HYDROS app.
//   - dosers (active, dosed_today_ml, reservoir_ml) — closes the loop
//     on calculator dose-vs-actual and surfaces reservoir runway so a
//     keeper sees "B-Ionic A: 230 mL left ≈ 55 days" at a glance.
//
//   - outlets ("Equipment now": on/off and power draw, HydrosOutlets)
//   - water level and leak inputs, as raw values beside the probes.
// Only temp, pH, salinity and alk become tank readings (hydros/ingest.js);
// the rest is shown here and nowhere else. Every value is shown in the
// keeper's units (°C, S.G.) through probeReading.
//
// Renders nothing when there's no controller linked: the card vanishes
// instead of showing an empty "no controller" state, so the user only
// sees this slot in their Home layout when it actually carries data.

const POLL_MS = 30_000;

// HYDROS alert levels per spec (decode.js ALERT_LEVELS). 0 = None, 1 =
// Yellow, 4 = Orange, 8 = Red. Display firmware switches on these
// exact ints; the UI uses the same mapping for parity.
const ALERT_TONE = { 1: 'yellow', 4: 'orange', 8: 'red' };
const ALERT_LABEL = { 1: 'Yellow', 4: 'Orange', 8: 'Red' };

// Worst (highest severity) alert level across all probe values. Drives
// the controller-level "Alert: ORANGE" pill at the top of the tile so a
// keeper notices a probe alarm even before scanning each value.
export function worstAlertLevel(values) {
  if (!Array.isArray(values)) return 0;
  let worst = 0;
  for (const v of values) {
    const a = Number(v?.alert);
    if (Number.isFinite(a) && a > worst) worst = a;
  }
  return worst;
}

// Doser reservoir runway from today's dosed-so-far. The HYDROS counter
// resets at midnight (controller-local), so a snapshot N hours into the
// day represents a partial day. We extrapolate to a 24h rate and divide
// the current reservoir by it — labeled "≈" because one day is not a
// stable rate, but it's enough to flag "you'll be out in a week" vs
// "you're fine for 3 months."
//
// Returns null when:
//   - reservoir is missing or zero (nothing to count down)
//   - dose rate is zero (active=false all day, or pump idle)
//   - now is < 1 h into the day (rate would be wildly unreliable)
//
// `since` is when the server began counting today's total (it sums its own
// day, not the phone's). Without it the phone's midnight is the best guess.
export function reservoirRunwayDays(reservoirMl, dosedTodayMl, now = new Date(), since = null) {
  if (!Number.isFinite(reservoirMl) || reservoirMl <= 0) return null;
  if (!Number.isFinite(dosedTodayMl) || dosedTodayMl <= 0) return null;
  let dayStart = since ? new Date(since) : null;
  if (!dayStart || !Number.isFinite(dayStart.getTime()) || dayStart > now) {
    dayStart = new Date(now);
    dayStart.setHours(0, 0, 0, 0);
  }
  const dayFraction = (now.getTime() - dayStart.getTime()) / (24 * 60 * 60 * 1000);
  if (dayFraction < 1 / 24) return null;
  const ratePerDay = dosedTodayMl / dayFraction;
  return reservoirMl / ratePerDay;
}

// Controller mode → pill tone. Away is the interesting one: the keeper
// is intentionally hands-off, dosers might be paused, and Sensei should
// soften the daily nags. Auto / Manual are calm/neutral.
const MODE_TONES = {
  auto: { tone: 'auto', label: 'Auto' },
  automatic: { tone: 'auto', label: 'Auto' },
  manual: { tone: 'manual', label: 'Manual' },
  away: { tone: 'away', label: 'Away' },
  vacation: { tone: 'away', label: 'Away' },
};
export function modeChip(mode) {
  if (!mode || typeof mode !== 'string') return null;
  const hit = MODE_TONES[mode.toLowerCase().trim()];
  return hit || { tone: 'manual', label: mode };
}

// Sort outlets for the strip: ones that are ON first, then by power
// desc (so the heavy hitter — return pump / heater — leads), then by
// name for stable ordering across renders.
export function sortOutlets(outlets) {
  if (!Array.isArray(outlets)) return [];
  return outlets
    .filter((o) => o && typeof o.name === 'string')
    .slice()
    .sort((a, b) => {
      const ao = a.on ? 0 : 1;
      const bo = b.on ? 0 : 1;
      if (ao !== bo) return ao - bo;
      const ap = Number.isFinite(a.power_w) ? a.power_w : -1;
      const bp = Number.isFinite(b.power_w) ? b.power_w : -1;
      if (ap !== bp) return bp - ap;
      return (a.name || '').localeCompare(b.name || '');
    });
}

function fmtRunway(days) {
  if (days == null || !Number.isFinite(days)) return '';
  if (days < 1) return '< 1 day';
  if (days < 14) return `≈ ${Math.round(days)} days`;
  if (days < 60) return `≈ ${Math.round(days / 7)} weeks`;
  return `≈ ${Math.round(days / 30)} months`;
}

export function HydrosLiveTile({ tank }) {
  const [link, setLink] = useState(null); // null = loading, {} = no link or no data
  const [err, setErr] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!tank?.id || !tank?.hydros_thing_id) {
      setLink({});
      return undefined;
    }
    let cancelled = false;

    const tick = async () => {
      try {
        const r = await api(`/api/tanks/${tank.id}/hydros`);
        if (!cancelled) {
          setLink(r);
          setErr(false);
        }
      } catch {
        if (!cancelled) setErr(true);
      }
    };

    const schedule = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        if (cancelled) return;
        if (!document.hidden) await tick();
        schedule();
      }, POLL_MS);
    };

    tick();
    schedule();
    const onVis = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [tank?.id, tank?.hydros_thing_id]);

  if (!tank?.hydros_thing_id) return null;
  if (link === null) {
    return (
      <div className="hv-hydros-tile" aria-busy="true">
        <div className="hv-hydros-head">
          <h3 className="font-heading">HYDROS</h3>
          <span className="ts-hint">connecting…</span>
        </div>
      </div>
    );
  }
  if (link?.stale || !link?.controller) {
    return (
      <div className="hv-hydros-tile">
        <div className="hv-hydros-head">
          <h3 className="font-heading">HYDROS controller</h3>
          <span className="ts-hint">no data yet</span>
        </div>
      </div>
    );
  }

  const online = !!link.controller.online;
  const name = link.controller.name || 'HYDROS';
  const { temp: tempUnit } = getDisplayUnits(tank);
  const mode = link.controller.mode || null;
  const values = Array.isArray(link.values) ? link.values : [];
  const dosers = Array.isArray(link.dosers) ? link.dosers : [];
  const outlets = Array.isArray(link.outlets) ? link.outlets : [];
  const ago = link.controller.last_seen_s;
  const worst = worstAlertLevel(values);
  const salinityUnit = readSalinityUnit();
  const labels = tileLabels(values);

  return (
    <div className={`hv-hydros-tile${online ? '' : ' offline'}`}>
      <div className="hv-hydros-head">
        <h3 className="font-heading">
          <span className={`hv-hydros-dot${online ? ' online' : ''}`} aria-hidden="true" />
          {name}
        </h3>
        <div className="hv-hydros-head-meta">
          {(() => {
            const m = modeChip(mode);
            return m ? <span className={`hv-hydros-mode tone-${m.tone}`}>{m.label}</span> : null;
          })()}
          <span className="ts-hint">
            {online ? 'Online' : 'Offline'}
            {ago != null && ` · ${fmtAgo(ago)} ago`}
            {err && ' · sync paused'}
          </span>
        </div>
      </div>
      {worst > 0 && (
        <div
          className={`hv-hydros-alert tone-${ALERT_TONE[worst]}`}
          role="status"
          aria-label={`HYDROS alert: ${ALERT_LABEL[worst]}`}
        >
          <span className="hv-hydros-alert-dot" aria-hidden="true" />
          HYDROS alert · {ALERT_LABEL[worst]}
        </div>
      )}
      <ProbePickNote tank={tank} link={link} />
      {values.length > 0 ? (
        <div className="hv-hydros-vals">
          {values.map((v, i) => {
            const tone = ALERT_TONE[Number(v.alert)] || null;
            const r = probeReading(v, { tempUnit, salinityUnit });
            return (
              <div
                key={`${v.key}-${i}`}
                className={`hv-hydros-val${tone ? ` tone-${tone}` : ''}`}
                title={tone ? `${ALERT_LABEL[Number(v.alert)]} alert` : undefined}
              >
                <span className="hv-hydros-label font-heading">{labels[i]}</span>
                <span className="hv-hydros-num font-mono">
                  {r.num}
                  {r.unit ? ` ${r.unit}` : ''}
                </span>
                {tone && (
                  <span className={`hv-hydros-alert-pill tone-${tone}`}>
                    {ALERT_LABEL[Number(v.alert)]}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ) : link.needs_choice?.length ? null : (
        <p className="ts-hint">Waiting for probe values…</p>
      )}
      <HydrosDosers dosers={dosers} />
      <HydrosOutlets outlets={outlets} />
    </div>
  );
}

// A key-connected Collective that feeds more than one tank, or carries two
// probes of one kind, reads nothing for those parameters until the keeper
// picks which probes are this tank's. Without this the tile just waited
// (owner, 2026-10-01: a display + frag tank keeper got no readings and no
// word why).
function ProbePickNote({ tank, link }) {
  const msg = probePickMessage(link, tank.name, tank.params);
  if (!msg) return null;
  return (
    <div className="hv-hydros-pick" role="status">
      <p>
        <strong>{msg.lead}</strong> {msg.rest}
      </p>
      {msg.canPick && (
        <button
          type="button"
          className="hk-btn hk-btn-primary"
          onClick={() =>
            window.dispatchEvent(
              new CustomEvent(OPEN_SETTINGS_EVENT, {
                detail: { section: 'equipment', focus: 'hydros' },
              })
            )
          }
        >
          Pick probes
        </button>
      )}
    </div>
  );
}

// Equipment-on-now strip. Each pill shows the outlet name, an on/off
// dot, and the live power draw when present. Sorted on-first so a
// reefer's eye lands on what's currently running. Hidden entirely when
// the controller has no outlets configured — most reef controllers do,
// but the in-memory store still occasionally arrives empty.
function HydrosOutlets({ outlets }) {
  const sorted = sortOutlets(outlets);
  if (!sorted.length) return null;
  return (
    <div className="hv-hydros-outlets">
      <div className="hv-hydros-outlets-head font-heading">Equipment now</div>
      <div className="hv-hydros-outlet-list">
        {sorted.map((o, i) => (
          <span
            key={`${o.name || 'outlet'}-${i}`}
            className={`hv-hydros-outlet${o.on ? ' on' : ''}`}
            title={Number.isFinite(o.power_w) ? `${o.power_w} W` : o.on ? 'On' : 'Off'}
          >
            <span className="hv-hydros-outlet-dot" aria-hidden="true" />
            <span className="hv-hydros-outlet-name">{o.name}</span>
            {Number.isFinite(o.power_w) && o.on && (
              <span className="hv-hydros-outlet-watts font-mono">{Math.round(o.power_w)}W</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

// Active dosing rendering — surfaces what the controller has actually
// pushed today + how much reservoir is left, so a keeper sees dosing
// without leaving Home. Only renders dosers carrying real data; an
// empty list (controller has dosers configured but nothing has dosed
// today and no reservoir is reported) hides the section entirely.
function HydrosDosers({ dosers }) {
  const interesting = (dosers || []).filter(
    (d) =>
      Number.isFinite(d?.dosed_today_ml) || Number.isFinite(d?.reservoir_ml) || d?.active === true
  );
  if (!interesting.length) return null;
  return (
    <div className="hv-hydros-dosers">
      <div className="hv-hydros-dosers-head font-heading">Dosing today</div>
      <ul className="hv-hydros-doser-list">
        {interesting.map((d, i) => {
          const today =
            Number.isFinite(d.dosed_today_ml) && d.dosed_today_ml > 0
              ? `${d.dosed_today_ml.toFixed(1)} mL`
              : d.active
                ? 'running…'
                : '—';
          const reservoir = Number.isFinite(d.reservoir_ml)
            ? `${Math.round(d.reservoir_ml)} mL left`
            : null;
          const runway = fmtRunway(
            reservoirRunwayDays(d.reservoir_ml, d.dosed_today_ml, new Date(), d.dosed_today_since)
          );
          return (
            <li key={`${d.name || 'doser'}-${i}`} className="hv-hydros-doser">
              <span className="hv-hydros-doser-name">{d.name || `Pump ${i + 1}`}</span>
              <span className="hv-hydros-doser-today font-mono">{today}</span>
              {reservoir && (
                <span className="hv-hydros-doser-runway font-mono">
                  {reservoir}
                  {runway ? ` · ${runway}` : ''}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function fmtAgo(seconds) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}
